<!-- eslint-disable @angular-eslint/template/cyclomatic-complexity -->
<header
  class="sticky top-0 z-50 transition-all duration-300"
  [class.bg-white]="isSticky() && !isMenuOpen()"
  [class.dark:bg-surface-dark]="isSticky() && !isMenuOpen()"
  [class.shadow-md]="isSticky() && !isMenuOpen()"
  [class.bg-transparent]="!isSticky() && !isMenuOpen()"
  [class.bg-muted]="isMenuOpen()"
  [class.dark:bg-page-dark]="isMenuOpen()"
>
  <div class="container">
    <!-- FEC-1179: from `lg` (where the desktop nav replaces the hamburger) up to
         `2xl` the row was wider than the viewport, so every page scrolled
         sideways. Spacing now steps through three bands — tightest at `lg`,
         easing at `xl`, and back to the designed values at `2xl` (1440px, the
         container's own max width). -->
    <div
      class="flex flex-row justify-between items-center gap-1 xl:gap-2 2xl:gap-0 pl-1 pr-1 xl:pl-2 xl:pr-2 2xl:pl-4 2xl:pr-2 pt-2 pb-6 rounded-[349px]"
      [class.pb-6]="!isSticky()"
      [class.pb-2]="isSticky()"
    >
      <a
        routerLink="/"
        aria-label="Far East Consortium — home"
        class="shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
      >
        <img
          [src]="
            themeService.theme() === 'dark'
              ? '../../../assets/images/svg/agent-access-light.svg'
              : '../../../assets/images/svg/agent-access.svg'
          "
          class="h-9 w-auto md:h-[48px] md:w-[207.4px] lg:h-9 lg:w-[140px] xl:w-[170px] 2xl:h-[48px] 2xl:w-[207.4px] cursor-pointer mr-2 md:mr-0"
          alt="fec-company-logo"
        />
      </a>
      <a
        routerLink="/"
        aria-label="Far East Consortium — home"
        class="hidden md:block lg:hidden shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
      >
        <img
          src="../../../assets/images/svg/logo-responsive.svg"
          class="h-[38px] w-auto cursor-pointer"
          alt="fec-company-logo"
        />
      </a>
      <nav class="hidden lg:block min-w-0" aria-label="Primary">
        <div
          class="bg-white dark:bg-surface-dark flex items-center gap-0 p-1 xl:gap-1.5 xl:p-1.5 2xl:gap-3 2xl:p-2 rounded-[43px]"
        >
          @for (item of userMenuList(); track $index) {
            @if (item.type === 'dropdown' && item.key === 'project') {
              <app-dropdown-menu #projectDropdown placement="bottom-start" menuWidth="w-[475px]">
                <div
                  *hasRole="item.roles || ''"
                  class="relative flex items-center gap-0.5 px-2 py-2.5 xl:px-3 2xl:px-4 2xl:py-3 rounded-[35px] text-dark dark:text-white transition-fec cursor-pointer"
                  [ngClass]="{ '!text-white bg-black [&>img]:invert': isLinkActive(item) }"
                >
                  <span class="tracking-[0.28px] text-sm">
                    {{ item.title }}
                  </span>
                  @if (item.count) {
                    <!-- Count badge — designer spec: red pill at the item's top-right corner. -->
                    <span
                      class="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 text-[11px] font-semibold leading-none rounded-full"
                      [class.bg-primary]="!isLinkActive(item)"
                      [class.text-white]="!isLinkActive(item)"
                      [class.bg-white]="isLinkActive(item)"
                      [class.text-dark]="isLinkActive(item)"
                    >
                      {{ item.count }}
                    </span>
                  }
                  <img
                    src="../../../assets/images/svg/chevron.svg"
                    alt="chevron"
                    class="w-4"
                    [class.invert]="isLinkActive(item)"
                    [class.dark:invert]="!isLinkActive(item)"
                  />
                </div>
                <div menuContent>
                  <app-project-navigator-dropdown
                    [enabled]="projectDropdown.isOpen()"
                    (itemSelected)="projectDropdown.close()"
                  />
                </div>
              </app-dropdown-menu>
            } @else {
              <a
                *hasRole="item.roles || ''"
                #rla="routerLinkActive"
                class="relative px-2 py-2.5 xl:px-3 2xl:px-4 2xl:py-3 rounded-[47px] text-dark dark:text-white transition-fec cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
                [routerLink]="item.link"
                [routerLinkActiveOptions]="{ exact: item?.isExact || false }"
                [routerLinkActive]="'!text-white bg-black'"
                [attr.aria-current]="rla.isActive ? 'page' : null"
              >
                <span class="tracking-[0.28px] text-sm">
                  {{ item.title }}
                </span>
                @if (item.count) {
                  <!-- Count badge — designer spec: red pill at the item's top-right corner. -->
                  <span
                    class="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 text-[11px] font-semibold leading-none rounded-full bg-primary text-white"
                  >
                    {{ item.count }}
                  </span>
                }
              </a>
            }
          }
        </div>
      </nav>
      <div class="flex items-center gap-1 2xl:gap-2 shrink-0">
        <!-- The Add action is a page action, not a navigation one: while the
             responsive menu is open it only crowds the logo, so it steps aside. -->
        @if (
          !isMenuOpen() &&
          (userPermissionService.isUserDeveloper() ||
            userPermissionService.isUserMasterAgent() ||
            userPermissionService.isUserAgencyManager())
        ) {
          @if (activeActionItem(); as activeAction) {
            <!--
              Regular Add button — icon only on mobile and again across the
              `lg`–`xl` band, where the label is exactly what pushed the row
              past the viewport (FEC-1179); the full text returns at `xl`.
              Users & Agencies host their own Add button in the Filter row, so the
              header action is suppressed there (key === 'user' | 'agency').
            -->
            @if (activeAction.key !== 'user' && activeAction.key !== 'agency') {
              <!--
                Add Project is desktop-only: below `lg` it crowded the logo out
                of the header, and the responsive menu now carries its own "+"
                beside the project search (FEC-1150 QA). Every other Add action
                keeps its icon-only button at all widths.
              -->
              <div
                [class]="
                  activeAction.key === 'project'
                    ? 'hidden lg:flex items-center'
                    : 'flex items-center'
                "
              >
                <app-button
                  layout="secondary"
                  class="[&>button]:hover:!bg-dark [&>button]:hover:!text-white [&>button]:border [&>button]:border-transparent [&>button]:hover:!border-dark"
                  className="gap-1 !px-2 md:!px-3 2xl:!px-4 !py-2 md:!py-2.5 lg:!py-[21px] !rounded-full"
                  [routerLink]="activeAction.addLink"
                >
                  <img
                    src="../../../assets/images/svg/plus.svg"
                    alt="add"
                    class="size-5 md:size-6"
                  />
                  <span class="hidden md:inline lg:hidden xl:inline"
                    >Add {{ activeAction.key }}</span
                  >
                </app-button>
              </div>
            }
          }
        }
        <!-- <div class="hidden lg:flex items-center gap-2">
       <div class="bg-white dark:bg-surface-dark flex items-center justify-center rounded-full size-11 cursor-pointer">
         <img src="../../../assets/images/svg/search.svg" alt="search" class="size-5" />
       </div>
     </div> -->
        <!-- Announcement bell — every signed-in role gets one (FEC-1161 QA). -->
        <app-announcement-bell />
        <!-- Theme toggle — standalone icon before the profile avatar. -->
        <button
          type="button"
          class="flex items-center justify-center bg-white rounded-full size-9 md:size-11 cursor-pointer transition-fec hover:bg-muted dark:bg-dark dark:hover:bg-dark-grey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
          [attr.aria-label]="
            themeService.theme() === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
          "
          [attr.aria-pressed]="themeService.theme() === 'dark'"
          title="Toggle theme"
          (click)="themeService.toggleTheme()"
        >
          @if (themeService.theme() === 'dark') {
            <!-- Sun icon — shown in dark mode, clicking switches to light -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="size-5 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="4"></circle>
              <path d="M12 2v2"></path>
              <path d="M12 20v2"></path>
              <path d="m4.93 4.93 1.41 1.41"></path>
              <path d="m17.66 17.66 1.41 1.41"></path>
              <path d="M2 12h2"></path>
              <path d="M20 12h2"></path>
              <path d="m6.34 17.66-1.41 1.41"></path>
              <path d="m19.07 4.93-1.41 1.41"></path>
            </svg>
          } @else {
            <!-- Moon icon — shown in light mode, clicking switches to dark -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="size-5 text-dark dark:text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          }
        </button>
        <app-dropdown-menu [items]="menuItems()" menuWidth="w-36">
          <div
            class="flex items-center gap-1.5 2xl:gap-2 bg-white dark:bg-surface-dark p-1 lg:pl-1.5 lg:pr-2 lg:py-1.5 2xl:pl-2 2xl:pr-4 2xl:py-2 rounded-[57px] cursor-pointer"
          >
            <app-user-avatar
              [users]="userList()"
              [displayLimit]="1"
              [allowTooltip]="true"
              className="scale-90 md:scale-100"
            />
            <div class="hidden lg:flex items-center gap-1 2xl:gap-2 min-w-0">
              <div class="flex flex-col gap-0.5 justify-center min-w-0">
                <p
                  class="text-sm font-semibold text-dark dark:text-white leading-[normal] truncate max-w-[100px] xl:max-w-[140px] 2xl:max-w-[180px]"
                >
                  {{ userList()[0]?.name || '' }}
                </p>
                <p class="text-xxs leading-tight text-dark-grey dark:text-grey truncate">
                  {{ userPermissionService.userRole()?.name | titlecase }}
                </p>
              </div>
              <app-icon name="chevron" [size]="16" class="text-dark dark:text-white" />
            </div>
          </div>
        </app-dropdown-menu>
        <button
          type="button"
          class="flex lg:hidden items-center justify-center size-9 md:size-11 shrink-0 cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
          [attr.aria-label]="isMenuOpen() ? 'Close menu' : 'Open menu'"
          [attr.aria-expanded]="isMenuOpen()"
          aria-controls="mobile-menu"
          (click)="toggleMenu()"
        >
          <!-- The close glyph fills its whole artboard while the hamburger sits
               inside a circle, so at the same box size the cross read far
               bigger than every other header icon — it renders inset instead
               (FEC-1150 QA). -->
          <img
            [src]="
              isMenuOpen()
                ? '../../../assets/images/svg/humburg close.svg'
                : '../../../assets/images/svg/button.svg'
            "
            [class]="isMenuOpen() ? 'size-6 md:size-7' : 'size-9 md:size-11'"
            alt=""
          />
        </button>
      </div>
    </div>
    <!-- ═══ Responsive main menu (FEC-1150) ═══
         Full-screen sheet on the page background, holding a single white card.
         Every entry is an icon + label row; Projects expands the project
         navigator inline instead of opening a second-level popover. -->
    @if (isMenuOpen()) {
      <nav
        id="mobile-menu"
        aria-label="Mobile"
        class="fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto bg-muted px-4 pb-6 dark:bg-page-dark"
      >
        <ul
          class="list-none flex w-full flex-col justify-start items-stretch gap-6 rounded-xl bg-white p-6 dark:bg-surface-dark"
        >
          @for (item of userMenuList(); track $index) {
            @if (item.type === 'dropdown' && item.key === 'project') {
              <li *hasRole="item.roles || ''" class="flex flex-col justify-start items-stretch">
                <button
                  type="button"
                  class="flex w-full flex-row justify-between items-center gap-2 text-left text-dark dark:text-white cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
                  [attr.aria-expanded]="isProjectsPanelOpen()"
                  aria-controls="mobile-menu-projects"
                  (click)="toggleProjectsPanel()"
                >
                  <span class="flex flex-row items-center gap-2">
                    <app-icon [name]="item.icon" [size]="20" />
                    <span class="text-xl tracking-[-0.4px]">{{ item.title }}</span>
                    @if (item.count) {
                      <span
                        class="inline-flex items-center justify-center min-w-5 h-[18px] px-1.5 text-xs font-medium rounded-full bg-primary text-white"
                      >
                        {{ item.count }}
                      </span>
                    }
                  </span>
                  <app-icon
                    name="chevron"
                    [size]="16"
                    class="transition-transform"
                    [class.rotate-180]="isProjectsPanelOpen()"
                  />
                </button>
                @if (isProjectsPanelOpen()) {
                  <div id="mobile-menu-projects" class="mt-6">
                    <app-project-navigator-dropdown
                      variant="inline"
                      [enabled]="isProjectsPanelOpen()"
                      [showAddProject]="canAddProject()"
                      (itemSelected)="closeMenu()"
                    />
                  </div>
                }
              </li>
            } @else {
              <li *hasRole="item.roles || ''">
                <a
                  #rla="routerLinkActive"
                  class="flex flex-row items-center gap-2 text-dark dark:text-white transition-fec cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
                  [routerLink]="item.link"
                  (click)="closeMenu()"
                  [routerLinkActiveOptions]="{ exact: item?.isExact || false }"
                  [routerLinkActive]="'font-medium'"
                  [attr.aria-current]="rla.isActive ? 'page' : null"
                >
                  <app-icon [name]="item.icon" [size]="20" />
                  <span class="text-xl tracking-[-0.4px]">{{ item.title }}</span>
                  @if (item.count) {
                    <span
                      class="inline-flex items-center justify-center min-w-5 h-[18px] px-1.5 text-xs font-medium rounded-full bg-primary text-white"
                    >
                      {{ item.count }}
                    </span>
                  }
                </a>
              </li>
            }
          }
        </ul>
      </nav>
    }
  </div>
</header>
